import {Component, OnDestroy} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router, RouterEvent,} from '@angular/router';

import {Subscription, Subject} from 'rxjs';
import {distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

/**
 * Interface for the BreadCrumb object.
 */
export interface BreadCrumb {
  label: string;
  url: string;
}

@UntilDestroy()
@Component({
  selector: 'ds-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
})
/** Class representing Breadcrumbs component. */
export class BreadcrumbComponent {

  /**
   * Listens for router events, filters them to action only on NavigationEnd.
   * Initiate the breadcrumb creation based on routing.
   */
  constructor(private router: Router, private activatedRoute: ActivatedRoute) {
    this.navigationEndSubscription = this.router.events
      .pipe(
        filter((event: RouterEvent) => event instanceof NavigationEnd),
        distinctUntilChanged()
      )
      .pipe(untilDestroyed(this)).subscribe(() => {
        this.breadcrumbs = this.buildBreadCrumb(this.activatedRoute.root);
      });
  }

  public breadcrumbs: BreadCrumb[];

  private navigationEndSubscription: Subscription;

  /**
   * Builds the breadcrumb based on routing.
   */
  private buildBreadCrumb(
    route: ActivatedRoute,
    url: string = '',
    breadcrumbs: BreadCrumb[] = []
  ): BreadCrumb[] {
    let label =
      route.routeConfig && route.routeConfig.data
        ? route.routeConfig.data.breadcrumb
        : '';
    let path =
      route.routeConfig && route.routeConfig.data ? route.routeConfig.path : '';

    path = path
      .split('/')
      .map((routePart) => {
        const isDynamicRoute = routePart.startsWith(':');
        if (isDynamicRoute && !!route.snapshot) {
          const paramName = routePart.split(':').pop();
          const paramValue = route.snapshot.params[paramName];

          label = `${label}(${paramValue})`;
          return paramValue;
        }
        return routePart;
      })
      .join('/');

    const nextUrl = path ? `${url}/${path}` : url;

    const breadcrumb: BreadCrumb = {
      label,
      url: nextUrl,
    };

    const newBreadcrumbs = breadcrumb.label
      ? [...breadcrumbs, breadcrumb]
      : [...breadcrumbs];

    return route.firstChild
      ? this.buildBreadCrumb(route.firstChild, nextUrl, newBreadcrumbs)
      : newBreadcrumbs;
  }
}
